/**
 * ChangeImpactForm — create + lifecycle for CR-2026-DC-IMPACT-REVIEW (D-032 §10).
 *
 * Redesigned 2026-05-05 (Phase 2.5, Class P) onto the PaperForm helper
 * library. Lifecycle action bar + dialogs preserved.
 */

import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Stack, Typography, Button, Alert, Chip, Box, Grid,
  TextField, MenuItem, Breadcrumbs, Link as MuiLink, IconButton,
  Table, TableHead, TableBody, TableRow, TableCell,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from "@mui/material";
import {
  HomeRounded, ArrowBackRounded, PlayArrowRounded, GavelRounded,
  PauseCircleRounded, PlayCircleRounded, CheckCircleOutlineRounded,
  CancelRounded, AddCircleOutlineRounded, RuleRounded,
} from "@mui/icons-material";

import {
  getImpact, createImpact, startImpactReview, dispositionImpact,
  placeImpactHold, liftImpactHold, closeImpact, cancelImpact,
  IMPACT_RISK, IMPACT_DISPOSITIONS, AFFECTED_STAGES, allowedImpactTransitions,
} from "../../utils/changeImpactsApi";
import {
  PaperFormCard, PaperFormHeader, PaperFormSection, PaperFormFooter,
  FieldLabel,
} from "../../components/PaperForm";

export default function ChangeImpactForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [params] = useSearchParams();
  const isCreate = !id || id === "new";

  const [ci, setCi] = useState(null);
  const [jobId, setJobId] = useState(params.get("job_id") || "");
  const [documentId, setDocumentId] = useState(params.get("document_id") || "");
  const [supersededDocumentId, setSupersededDocumentId] = useState("");
  const [drawingRev, setDrawingRev] = useState(params.get("rev") || "");
  const [changeSummary, setChangeSummary] = useState("");
  const [riskLevel, setRiskLevel] = useState("MEDIUM");
  const [affectedDraft, setAffectedDraft] = useState([]);

  const [loading, setLoading] = useState(!isCreate);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // dialogs
  const [openDisp, setOpenDisp] = useState(false);
  const [disposition, setDisposition] = useState("");
  const [dispReason, setDispReason] = useState("");
  const [openHold, setOpenHold] = useState(false);
  const [holdReason, setHoldReason] = useState("");

  const refresh = () => {
    if (isCreate) return;
    setLoading(true);
    getImpact(id)
      .then((data) => setCi(data))
      .catch((err) => setError(err?.response?.data?.error || err?.message))
      .finally(() => setLoading(false));
  };
  useEffect(refresh, [id, isCreate]);

  const handleAddAffected = () => setAffectedDraft([...affectedDraft, { affected_stage: "ISSUED" }]);
  const handleAffectedChange = (idx, key, value) => {
    const next = [...affectedDraft]; next[idx] = { ...next[idx], [key]: value }; setAffectedDraft(next);
  };
  const handleRemoveAffected = (idx) => setAffectedDraft(affectedDraft.filter((_, i) => i !== idx));

  const handleCreate = async () => {
    setError(null);
    if (!jobId || !documentId || !drawingRev || !changeSummary.trim()) {
      setError("Job ID, Document ID, Drawing Revision, and Change Summary are all required.");
      return;
    }
    setBusy(true);
    try {
      const created = await createImpact({
        job_id: Number(jobId),
        document_id: Number(documentId),
        superseded_document_id: supersededDocumentId ? Number(supersededDocumentId) : null,
        trigger_drawing_revision: drawingRev,
        change_summary: changeSummary,
        risk_level: riskLevel,
        affected_pieces: affectedDraft.filter((a) => a.affected_stage),
      });
      navigate(`/change-impacts/${created.id}`);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message);
    } finally { setBusy(false); }
  };

  const fireAction = async (fn, ...args) => {
    setBusy(true); setError(null);
    try { await fn(...args); refresh(); }
    catch (err) { setError(err?.response?.data?.error || err?.message); }
    finally { setBusy(false); }
  };

  const status = ci?.status || (isCreate ? "OPEN" : "OPEN");
  const next = isCreate ? [] : allowedImpactTransitions(status);
  const headerNumber = ci?.id ? `#${ci.id}` : (isCreate ? "Assigned automatically" : `#${id}`);
  const headerDate = useMemo(() => {
    if (ci?.created_at) return new Date(ci.created_at).toLocaleDateString();
    return new Date().toLocaleDateString();
  }, [ci]);

  if (loading) {
    return (
      <Box sx={{ pt: 6, textAlign: "center" }}>
        <Typography color="text.secondary">Loading impact review…</Typography>
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title>{isCreate ? "New Change Impact" : `Impact Review #${id}`} | SFM</title>
      </Helmet>

      <Box sx={{ bgcolor: "#fafafa", minHeight: "100vh" }}>
        <Box sx={{ maxWidth: "lg", mx: "auto", pt: 3, px: { xs: 2, md: 3 } }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Breadcrumbs>
              <MuiLink href="/" color="inherit" underline="hover">
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <HomeRounded fontSize="small" /><span>Home</span>
                </Stack>
              </MuiLink>
              <MuiLink href="/change-impacts" color="inherit" underline="hover">
                Change Impact Reviews
              </MuiLink>
              <Typography color="text.primary">{isCreate ? "New" : `#${id}`}</Typography>
            </Breadcrumbs>
            <IconButton onClick={() => navigate("/change-impacts")} aria-label="Back">
              <ArrowBackRounded />
            </IconButton>
          </Stack>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {!isCreate && status === "DISPOSITIONED" && ci?.disposition && (
            <Alert severity="success" sx={{ mb: 2 }}>
              <strong>Disposition:</strong> {ci.disposition} — {ci.disposition_reason}
            </Alert>
          )}
        </Box>

        <PaperFormCard maxWidth="lg">
          <PaperFormHeader
            formId="SFM · Change Impact Review"
            title={isCreate ? "New Change Impact Review" : "Change Impact Review"}
            subtitle="AISC drawing-control — drawing-revision impact assessment"
            status={status}
            number={headerNumber}
            numberLabel="Review No."
            date={headerDate}
            icon="rule"
          />

          {/* HOLD ACTIVE indicator */}
          {!isCreate && ci?.hold_active === 1 && (
            <Alert severity="error" sx={{ mb: 3 }}>
              <strong>Hold active:</strong> this impact review is currently on hold. Lift the hold from the
              action bar at the bottom to resume.
            </Alert>
          )}

          {/* TRIGGER DOCUMENT IDENTIFICATION */}
          <Grid container spacing={4} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <PaperFormSection label="Trigger Document" bottomPadding={0}>
                <Box>
                  <FieldLabel required>Job ID</FieldLabel>
                  <TextField
                    fullWidth size="small" type="number"
                    value={jobId}
                    onChange={(e) => setJobId(e.target.value)}
                    disabled={!isCreate}
                    placeholder="e.g., 24-101"
                  />
                </Box>
                <Box>
                  <FieldLabel required hint="the new revision being reviewed">
                    Document ID (new revision)
                  </FieldLabel>
                  <TextField
                    fullWidth size="small" type="number"
                    value={documentId}
                    onChange={(e) => setDocumentId(e.target.value)}
                    disabled={!isCreate}
                  />
                </Box>
                <Box>
                  <FieldLabel hint="optional · prior current document being superseded">
                    Superseded Document ID
                  </FieldLabel>
                  <TextField
                    fullWidth size="small" type="number"
                    value={supersededDocumentId}
                    onChange={(e) => setSupersededDocumentId(e.target.value)}
                    disabled={!isCreate}
                  />
                </Box>
                <Box>
                  <FieldLabel required>Drawing Revision</FieldLabel>
                  <TextField
                    fullWidth size="small"
                    value={drawingRev}
                    onChange={(e) => setDrawingRev(e.target.value)}
                    disabled={!isCreate}
                    placeholder='e.g., "C"'
                  />
                </Box>
              </PaperFormSection>
            </Grid>

            <Grid item xs={12} md={6}>
              <PaperFormSection
                label="Risk & Summary"
                hint="risk level drives reviewer assignment"
                bottomPadding={0}
              >
                <Box>
                  <FieldLabel>Risk Level</FieldLabel>
                  <TextField
                    select fullWidth size="small"
                    value={riskLevel}
                    onChange={(e) => setRiskLevel(e.target.value)}
                    disabled={!isCreate}
                  >
                    {IMPACT_RISK.map((r) => (
                      <MenuItem key={r} value={r}>{r}</MenuItem>
                    ))}
                  </TextField>
                </Box>
                <Box>
                  <FieldLabel required hint="what changed and why it matters">
                    Change Summary
                  </FieldLabel>
                  <TextField
                    fullWidth multiline rows={5} size="small"
                    value={changeSummary}
                    onChange={(e) => setChangeSummary(e.target.value)}
                    disabled={!isCreate}
                    placeholder="What changed in this revision? e.g., 'Beam B-12 elevation moved 50mm; affects 11 pieces in fabrication and 3 loaded'"
                  />
                </Box>
              </PaperFormSection>
            </Grid>
          </Grid>

          {/* AFFECTED ITEMS — CREATE MODE */}
          {isCreate && (
            <PaperFormSection
              label="Affected Items"
              hint="pieces / WOs / heats / loads / tasks impacted by this change"
            >
              <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
                <Button
                  startIcon={<AddCircleOutlineRounded />}
                  onClick={handleAddAffected}
                  size="small"
                  sx={{ textTransform: "none" }}
                >
                  Add row
                </Button>
              </Stack>
              {affectedDraft.length === 0 ? (
                <Box
                  sx={{
                    p: 2.5, textAlign: "center",
                    border: "1px dashed", borderColor: "divider", borderRadius: 2,
                    bgcolor: "#fafafa",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    No affected items yet. Add rows here, or add via API after creating the review.
                  </Typography>
                </Box>
              ) : (
                <Table
                  size="small"
                  sx={{
                    "& th": {
                      fontSize: 11, fontWeight: 700, letterSpacing: "0.06em",
                      textTransform: "uppercase", color: "text.secondary",
                      bgcolor: "#fafafa",
                    },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell>Piece</TableCell>
                      <TableCell>WO</TableCell>
                      <TableCell>Heat</TableCell>
                      <TableCell>Load</TableCell>
                      <TableCell>Task</TableCell>
                      <TableCell>Stage</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {affectedDraft.map((a, idx) => (
                      <TableRow key={idx}>
                        <TableCell><TextField size="small" type="number" value={a.piece_id || ""} onChange={(e) => handleAffectedChange(idx, "piece_id", e.target.value)} /></TableCell>
                        <TableCell><TextField size="small" type="number" value={a.work_order_id || ""} onChange={(e) => handleAffectedChange(idx, "work_order_id", e.target.value)} /></TableCell>
                        <TableCell><TextField size="small" type="number" value={a.heat_id || ""} onChange={(e) => handleAffectedChange(idx, "heat_id", e.target.value)} /></TableCell>
                        <TableCell><TextField size="small" type="number" value={a.load_id || ""} onChange={(e) => handleAffectedChange(idx, "load_id", e.target.value)} /></TableCell>
                        <TableCell><TextField size="small" type="number" value={a.task_id || ""} onChange={(e) => handleAffectedChange(idx, "task_id", e.target.value)} /></TableCell>
                        <TableCell>
                          <TextField select size="small" value={a.affected_stage || "ISSUED"}
                            onChange={(e) => handleAffectedChange(idx, "affected_stage", e.target.value)}>
                            {AFFECTED_STAGES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                          </TextField>
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={() => handleRemoveAffected(idx)} aria-label="Remove row">
                            <CancelRounded fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </PaperFormSection>
          )}

          {/* AFFECTED ITEMS — EDIT MODE (read-only) */}
          {!isCreate && ci?.affected_pieces?.length > 0 && (
            <PaperFormSection
              label={`Affected Items (${ci.affected_pieces.length})`}
              hint="captured at create time · disposition recorded per piece"
            >
              <Table
                size="small"
                sx={{
                  "& th": {
                    fontSize: 11, fontWeight: 700, letterSpacing: "0.06em",
                    textTransform: "uppercase", color: "text.secondary",
                    bgcolor: "#fafafa",
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>Type</TableCell>
                    <TableCell>ID</TableCell>
                    <TableCell>Stage</TableCell>
                    <TableCell>Disposition</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ci.affected_pieces.map((p) => {
                    const items = [];
                    if (p.piece_id) items.push({ t: "Piece", v: p.piece_id });
                    if (p.work_order_id) items.push({ t: "WO", v: p.work_order_id });
                    if (p.heat_id) items.push({ t: "Heat", v: p.heat_id });
                    if (p.load_id) items.push({ t: "Load", v: p.load_id });
                    if (p.task_id) items.push({ t: "Task", v: p.task_id });
                    return items.map((it) => (
                      <TableRow key={`${p.id}-${it.t}`}>
                        <TableCell>{it.t}</TableCell>
                        <TableCell>#{it.v}</TableCell>
                        <TableCell>{p.affected_stage}</TableCell>
                        <TableCell>{p.piece_disposition || "—"}</TableCell>
                      </TableRow>
                    ));
                  })}
                </TableBody>
              </Table>
            </PaperFormSection>
          )}

          <PaperFormFooter
            citation="AISC drawing-control — change impact review"
          />

          {/* ACTION BAR */}
          <Stack direction="row" justifyContent="flex-end" spacing={1} flexWrap="wrap" sx={{ mt: 4 }}>
            {/* LIFECYCLE ACTIONS — edit mode */}
            {!isCreate && (
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ flex: 1 }}>
                {next.includes("IN_REVIEW") && status === "OPEN" && (
                  <Button
                    startIcon={<PlayArrowRounded />} variant="outlined" size="small"
                    onClick={() => fireAction(startImpactReview, id)} disabled={busy}
                    sx={{ textTransform: "none", borderRadius: 2 }}
                  >Start review</Button>
                )}
                {next.includes("DISPOSITIONED") && (
                  <Button
                    startIcon={<GavelRounded />} variant="outlined" color="success" size="small"
                    onClick={() => setOpenDisp(true)} disabled={busy}
                    sx={{ textTransform: "none", borderRadius: 2 }}
                  >Disposition</Button>
                )}
                {next.includes("HELD") && (
                  <Button
                    startIcon={<PauseCircleRounded />} color="error" variant="outlined" size="small"
                    onClick={() => setOpenHold(true)} disabled={busy}
                    sx={{ textTransform: "none", borderRadius: 2 }}
                  >Place hold</Button>
                )}
                {status === "HELD" && (
                  <Button
                    startIcon={<PlayCircleRounded />} variant="outlined" size="small"
                    onClick={() => {
                      const reason = window.prompt("Reason for lifting the hold (min 4 chars, kept in the audit trail):");
                      if (reason) fireAction(liftImpactHold, id, reason);
                    }}
                    disabled={busy}
                    sx={{ textTransform: "none", borderRadius: 2 }}
                  >Lift hold</Button>
                )}
                {next.includes("CLOSED") && (
                  <Button
                    startIcon={<CheckCircleOutlineRounded />} variant="contained" color="success" size="small"
                    onClick={() => fireAction(closeImpact, id)} disabled={busy}
                    sx={{ textTransform: "none", borderRadius: 2 }}
                  >Close</Button>
                )}
                {next.includes("CANCELLED") && (
                  <Button
                    startIcon={<CancelRounded />} size="small"
                    onClick={() => {
                      const reason = window.prompt("Cancel reason (min 4 chars):");
                      if (reason) fireAction(cancelImpact, id, reason);
                    }}
                    disabled={busy}
                    sx={{ textTransform: "none", color: "text.secondary" }}
                  >Cancel review</Button>
                )}
              </Stack>
            )}

            {/* CREATE actions */}
            {isCreate && (
              <>
                <Box sx={{ flex: 1 }} />
                <Button
                  variant="text" onClick={() => navigate("/change-impacts")} disabled={busy}
                  sx={{ color: "text.secondary" }}
                >Cancel</Button>
                <Button
                  variant="contained" startIcon={<RuleRounded />}
                  onClick={handleCreate} disabled={busy}
                  sx={{ px: 3, borderRadius: 2, textTransform: "none", fontWeight: 600 }}
                >
                  {busy ? "Creating…" : "Create Impact Review"}
                </Button>
              </>
            )}
          </Stack>
        </PaperFormCard>
      </Box>

      <Dialog open={openDisp} onClose={() => setOpenDisp(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Record disposition</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Box>
              <FieldLabel required>Disposition</FieldLabel>
              <TextField
                select fullWidth size="small"
                value={disposition}
                onChange={(e) => setDisposition(e.target.value)}
              >
                <MenuItem value=""><em>Select disposition…</em></MenuItem>
                {IMPACT_DISPOSITIONS.map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
              </TextField>
            </Box>
            <Box>
              <FieldLabel required hint="captured in audit_log">
                Reason (min 4 chars)
              </FieldLabel>
              <TextField
                fullWidth size="small" multiline rows={3}
                value={dispReason}
                onChange={(e) => setDispReason(e.target.value)}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDisp(false)} disabled={busy}>Cancel</Button>
          <Button variant="contained" onClick={async () => {
            await fireAction(dispositionImpact, id, { disposition, disposition_reason: dispReason });
            setOpenDisp(false); setDisposition(""); setDispReason("");
          }} disabled={busy || !disposition || dispReason.length < 4}>Record</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openHold} onClose={() => setOpenHold(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: "error.main" }}>Place hold</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <FieldLabel required hint="captured in audit_log">
              Hold Reason (min 4 chars)
            </FieldLabel>
            <TextField
              fullWidth size="small" multiline rows={3}
              value={holdReason}
              onChange={(e) => setHoldReason(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenHold(false)} disabled={busy}>Cancel</Button>
          <Button variant="contained" color="error" onClick={async () => {
            await fireAction(placeImpactHold, id, holdReason);
            setOpenHold(false); setHoldReason("");
          }} disabled={busy || holdReason.length < 4}>Place hold</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
